<template>
  <div class="relative flex flex-col z-10 overflow-hidden" :class="wrapperPaddingClasslist" :style="tileStyleList">
    <Printable
        :key="`printable-${tileIndex}`"
        :panels="panels"
        :width="selectedWidth"
        :height="selectedHeight"
        :edge-dimension="edgeDimension"
        :edge-color="edgeColor"
        :render-mode="renderMode"
        :frame="frameDimension"
        :gap="gapDimension"
        :mat="matDimension"
        :isMobile="isMobile"
        :selectedEditToolId="selectedEditToolId"
        :wrapperPaddingClasslist="wrapperPaddingClasslist"
        :tileIndex="tileIndex"
    >
      <Panes
        :key="`panes-${tileIndex}`"
        :panes="panes"
        :width="selectedWidth"
        :height="selectedHeight"
        :edge="edgeDimension"
        :edge-color="edgeColor"
        :border="borderDimension"
        :border-color="borderColorValue"
        :images="images"
        :events="!showPreview"
        :upload-later="uploadLater"
        :render-mode="renderMode"
        :is-art="isArt"
        :product-slug="productData.productSlug"
        :disable-collage-outer-border="disableCollageOuterBorder"
        :filter="liveFilterAvailable(filter) ? filter : null"
        :featureFlags="featureFlags"
        :selectedEditToolId="selectedEditToolId"
        :imageEditTrigger="imageEditTrigger"
        :locale="locale"
        :tileIndex="tileIndex"
        @paneClick="handlePaneClick"
      />
    </Printable>
    <Previews
        :key="`previews-${tileIndex}`"
        aria-hidden="true"
        tabindex="-1"
        :selectedEditToolId="selectedEditToolId"
        :events="showPreview"
        :visible="showPreview"
        :width="selectedWidth"
        :height="selectedHeight"
        :frame="frameDimension"
        :gap="gapDimension"
        :mat="matDimension"
        :render-mode="renderMode"
        :can-manipulate="productData.productSlug !== PRODUCT_ART_SINGLE_PANEL && productData.productSlug !== PRODUCT_COLLAGE_FRAMED_PRINT"
        :feature-flags="featureFlags"
        :can-edit="canEdit"
        :frame-color="frameColor"
        :preview="showPreview"
        :isMobile="isMobile"
        :isArt="isArt"
        :panels="panels"
        :edge="edgeDimension"
        :frameType="frame"
        :tileIndex="tileIndex"
        :tileLabel="tileLabel"
        :enableScaleAnimation="false"
    >
    </Previews>
  </div>
</template>

<script>

import { Printable, Panes, Previews, products } from '@canvaspop/scoops'
import {
  getPanels,
  getSelectedWidth,
  getSelectedHeight,
  getEdgeDimension,
  getFrame,
  getEdge,
  getEdgeColor,
  getFrameDimension,
  getGapDimension,
  getMatDimension,
  getBorderDimension,
  getBorder,
  getBorderColor,
  getBorderColorValue,
  getFrameColor,
  getFilter,
  getPanes,
  getTotalWidth,
  getTotalHeight
} from '@vuedir/utils/builder-utils'
import * as locales from '@vuedir/constants/locales'
/**
 * Tile.vue
 *
 */
export default {

  components: {
    Panes,
    Previews,
    Printable,
  },

  props: {
    product: {
      type: Object,
      required: true,
    },
    featureFlags: {
      type: Object,
      default: null
    },
    renderMode: {
      type: Boolean,
      default: false
    },
    uploadLater: {
      type: Boolean,
      default: false
    },
    canEdit: {
      type: Boolean,
      default: false
    },
    isMobile: {
      type: Boolean,
      default: false
    },
    showPreview: {
      type: Boolean,
      default: false
    },
    disableCollageOuterBorder: {
      type: Boolean,
      default: false
    },
    isArt: {
      type: Boolean,
      default: false
    },
    selectedEditToolId: {
      type: String,
      default: null
    },
    imageEditTrigger: {
      type: String,
      default: null
    },
    wrapperPaddingClasslist: {
      type: String,
      default: ''
    },
    locale: {
      type: String,
      default: locales.LOCALE_US
    },
    images: {
      type: Object,
      default: {}
    },
    tileIndex: {
      type: Number,
      default: 0,
    },
  },

  data () {
    return {
      customEdge: 0,
      PRODUCT_ART_SINGLE_PANEL: products.PRODUCT_ART_SINGLE_PANEL,
      PRODUCT_ART_FRAMED_PRINT: products.PRODUCT_ART_FRAMED_PRINT,
      PRODUCT_COLLAGE_FRAMED_PRINT: products.PRODUCT_COLLAGE_FRAMED_PRINT,
    }
  },

  emits: ['paneClick'],

  mounted () {
  },

  computed: {
    productData () {
      return this.product.productData
    },
    tileLabel () {
      return this.product.sizes[this.productData.size] ? this.product.sizes[this.productData.size].label : ''
    },
    panels () {
      return getPanels(this.productData.productSlug)
    },
    selectedWidth () {
      return getSelectedWidth(this.productData.size, this.productData.productSlug)
    },
    selectedHeight () {
      return getSelectedHeight(this.productData.size)
    },
    frame () {
      return getFrame(this.productData.options)
    },
    edge () {
      return getEdge(this.productData.options)
    },
    border () {
      return getBorder(this.productData.options)
    },
    borderColor () {
      return getBorderColor(this.productData.options)
    },
    filter () {
      return getFilter(this.productData.options)
    },
    edgeDimension () {
      return getEdgeDimension(this.productData.productSlug, this.frame, this.customEdge)
    },
    edgeColor () {
      return getEdgeColor(this.productData.productSlug)
    },
    frameDimension () {
      return getFrameDimension(this.productData.productSlug, this.frame)
    },
    gapDimension () {
      return getGapDimension(this.productData.productSlug, this.frame)
    },
    matDimension () {
      return getMatDimension(this.productData.productSlug, this.edge)
    },
    borderDimension () {
      return getBorderDimension(this.productData.productSlug, this.border)
    },
    borderColorValue () {
      return getBorderColorValue(this.productData.productSlug, this.borderColor)
    },
    frameColor () {
      return getFrameColor(this.productData.productSlug, this.frame)
    },
    panes () {
      return getPanes()
    },
    totalWidth () {
      return getTotalWidth(this.selectedWidth, this.edgeDimension)
    },
    totalHeight () {
      return getTotalHeight(this.selectedHeight, this.edgeDimension)
    },
    tileStyleList () {
      let desktopWidth = this.selectedWidth > this.selectedHeight ? '650px' : '500px'

      return {
        'width': this.isMobile ? '300px' : desktopWidth
      }
    },
    hasImages () {
      return Object.keys(this.images).length !== 0
    },
  },

  methods: {
    handlePaneClick (id) {
      this.$emit('paneClick', { paneId: id, tileIndex: this.tileIndex})
    },
    liveFilterAvailable (filter) {

      return ['BW', 'SP', 'LM', 'VT'].includes(filter) && this.hasImages
    },
  },
}
</script>
